import { useChatSubmit } from './useChatSubmit';
import { fillIdPlaceholders } from '../helpers';
import type { SuggestedAction } from '../types';

interface Args {
  /** Tool call that rendered this block. Sent with the targeted ids so the
   * backend can verify them against this exact persisted result. */
  toolCallId?: string;
  /** Every row's id, in render order. */
  allIds: string[];
  /** Ticked ids. Empty = the action targets the whole list. */
  selectedIds: string[];
  /** id → display name, for {first_name}/{recipients_phrase}. */
  nameById?: Record<string, string>;
  appIds?: string[];
  /** When set, a bare {first_id} renders as the target's name plus a hidden
   *  <ctx:ctxKey=id> marker — the bubble shows the name, the agent still gets
   *  the id. Job followups use this so "job 21480" reads as the job title. */
  idAsName?: { ctxKey: string; fallbackLabel: string };
}

// The follow-up chip contract every block shares: the selection drives the
// action (falling back to the whole list), and multi-target chips show the
// narrowed count.
export const useBlockFollowup = ({
  toolCallId,
  allIds,
  selectedIds,
  nameById,
  appIds,
  idAsName,
}: Args) => {
  const { send, isSubmitting } = useChatSubmit();
  const ids = selectedIds.length > 0 ? selectedIds : allIds;

  const sendFollowup = (template: string) => {
    if (isSubmitting) return;
    const names = ids.map((id) => nameById?.[id] ?? '');
    const verifiedIds = ids
      .map(Number)
      .filter((id) => Number.isSafeInteger(id) && id > 0)
      .slice(0, 50);
    void send(
      fillIdPlaceholders(
        template,
        ids,
        appIds,
        names,
        idAsName ? { idAsCtxName: idAsName } : undefined
      ),
      undefined,
      {
        // An explicit empty snapshot is intentional: block actions must never
        // inherit checkbox state from a different visible card.
        selectionSnapshot:
          toolCallId && verifiedIds.length > 0
            ? [{ tool_call_id: toolCallId, ids: verifiedIds }]
            : [],
      }
    );
  };

  const withCounts = (actions: SuggestedAction[] = []): SuggestedAction[] =>
    selectedIds.length === 0
      ? actions
      : actions.map((a) =>
          a.promptTemplate.includes('{ids}') ||
          a.promptTemplate.includes('{count}')
            ? { ...a, label: `${a.label} (${selectedIds.length})` }
            : a
        );

  return { sendFollowup, withCounts, isSubmitting };
};
